import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  searchProducts,
  setSearchTerm,
  setSelectedCategory,
  clearSearch,
  clearCategory,
  selectCategories,
  selectCategoriesLoading,
  selectProducts,
  selectProductsLoading,
  selectSearchTerm,
  selectSelectedCategory,
  selectTotalResults
} from '../../../../slice/search/searchSlice';
import { addToCart, fetchCart } from "../../../../slice/cart/cartSlice";

import {
  toggleFavourite,
  fetchFavourites
} from "../../../../slice/favourite/favouriteSlice";
import { toast } from "react-toastify";
import Popuplogin from "../../components/Popuplogin";
import './search.css';

const Search = ({ categories = [] }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const products = useSelector(selectProducts);
  const productsLoading = useSelector(selectProductsLoading);
  const searchTerm = useSelector(selectSearchTerm);
  const selectedCategory = useSelector(selectSelectedCategory);
  const totalResults = useSelector(selectTotalResults);
  const { user, token } = useSelector((state) => state.auth);
  const { loading: cartLoading } = useSelector((state) => state.cart);
  const { items: favourites, loading: favLoading } = useSelector((state) => state.favourite);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedProductColor, setSelectedProductColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [textInputs, setTextInputs] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [mainSize, setMainSize] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [viewImages, setViewImages] = useState([]);
  const [selectedViewImage, setSelectedViewImage] = useState(0);

  const defaultImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="500"%3E%3Crect fill="%23f0f0f0" width="400" height="500"/%3E%3Ctext x="50%25" y="50%25" font-size="20" fill="%23999" text-anchor="middle" dy=".3em"%3EYour Photo%3C/text%3E%3C/svg%3E';

  const API_URL = process.env.REACT_APP_API_URL;

  /* =============================================
     HELPER — safe product image with fallback
  ============================================= */
  const getProductImage = (product) => {
    if (product?.images && product.images.length > 0 && product.images[0]) {
      return `${API_URL}/uploads/${product.images[0]}`;
    }
    if (product?.previewImage) {
      return `${API_URL}/uploads/${product.previewImage}`;
    }
    return defaultImage;
  };



  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: totalResults || 0,
      totalPages: Math.ceil((totalResults || 0) / prev.limit)
    }));
  }, [totalResults]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      dispatch(searchProducts({
        searchTerm,
        category: selectedCategory,
        limit: pagination.limit,
        page: pagination.page
      }));
    }, 300);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchTerm, selectedCategory, pagination.limit, pagination.page, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (value) => {
    dispatch(setSelectedCategory(value));
    setIsDropdownOpen(false);
  };

  const handleSearchChange = (e) => dispatch(setSearchTerm(e.target.value));
  const handleClearSearch = () => dispatch(clearSearch());
  const handleClearCategory = () => dispatch(clearCategory());

  const getCategoryLabel = (value) => {
    const category = categories.find(cat => cat._id === value);
    return category ? category.name : '';
  };

  const getSelectedCategory = () => {
    return categories.find(cat => cat._id === selectedCategory) ||
      categories[0] ||
      { name: 'All Categories' };
  };

  const isFavourite = (productId) => {
    return favourites.some(
      (fav) => String(fav.product?._id) === String(productId)
    );
  };

  const handleFavouriteClick = async (e, productId) => {
    e.stopPropagation();
    if (!user || !token) { setShowLoginModal(true); return; }
    const alreadyFav = isFavourite(productId);
    try {
      await dispatch(toggleFavourite(productId)).unwrap();
      toast[alreadyFav ? "info" : "success"](
        alreadyFav ? "Removed from wishlist 💔" : "Added to wishlist ❤️"
      );
    } catch (error) {
      toast.error(error || "Something went wrong");
    }
  };

  const handleOpenCustomizer = (product) => {
    setSelectedProduct(product);
    setValidationErrors([]);

    if (product.sizes && product.sizes.length > 0) {
      setMainSize(product.sizes[0].name);
      setSelectedSize(product.sizes[0].name);
    }

    if (product.colors && product.colors.length > 0) {
      setSelectedProductColor(product.colors[0].code);
      setSelectedColor('');
    }

    setTextInputs(Array(product.textCount || 0).fill(''));
    setImages(Array(product.imageCount || 0).fill(''));
    setImageFiles(Array(product.imageCount || 0).fill(null));
    setCurrentImageIndex(0);
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    setValidationErrors([]);
  };

  const handleTextChange = (index, value) => {
    const newTexts = [...textInputs];
    newTexts[index] = value;
    setTextInputs(newTexts);
    setValidationErrors([]);
  };

  const handleImageUpload = (index, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const newFiles = [...imageFiles];
    newFiles[index] = file;
    setImageFiles(newFiles);

    const reader = new FileReader();
    reader.onload = (e) => {
      const newImages = [...images];
      newImages[index] = e.target.result;
      setImages(newImages);
    };
    reader.readAsDataURL(file);
    setValidationErrors([]);
  };

  const handleNextImage = () => {
    const uploadedImages = images.filter(img => img !== '');
    if (uploadedImages.length > 0)
      setCurrentImageIndex((prev) => (prev + 1) % uploadedImages.length);
  };

  const handlePrevImage = () => {
    const uploadedImages = images.filter(img => img !== '');
    if (uploadedImages.length > 0)
      setCurrentImageIndex((prev) => (prev - 1 + uploadedImages.length) % uploadedImages.length);
  };

  const getCurrentImage = () => {
    const uploadedImages = images.filter(img => img !== '');
    if (uploadedImages.length === 0) return defaultImage;
    return uploadedImages[currentImageIndex];
  };

  const hasMultipleImages = () => images.filter(img => img !== '').length > 1;

  const validateForm = () => {
    const errors = [];
    if (!selectedProduct) { errors.push("No product selected"); setValidationErrors(errors); return false; }
    if (selectedProduct.imageCount > 0) {
      const uploadedImages = images.filter(img => img !== '');
      if (uploadedImages.length < selectedProduct.imageCount)
        errors.push(`Please upload ${selectedProduct.imageCount} image(s)`);
    }
    if (selectedProduct.textCount > 0) {
      if (textInputs.some(text => !text.trim()))
        errors.push("Please fill in all text fields");
    }
    if (!mainSize) errors.push("Please select a frame size");
    if (!selectedColor || selectedColor.trim() === "") errors.push("Please select a frame color");
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const resetCustomizer = () => {
    setSelectedProduct(null);
    setSelectedColor("");
    setSelectedProductColor("");
    setSelectedSize("");
    setTextInputs([]);
    setImages([]);
    setImageFiles([]);
    setCurrentImageIndex(0);
    setMainSize("");
    setValidationErrors([]);
  };

  const handleAddToCart = () => {
    if (!user && !token) { setShowLoginModal(true); return; }
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append("productId", selectedProduct._id);
    formData.append("frameSize", mainSize);
    formData.append("frameColor", selectedColor);
    formData.append("productColor", selectedProductColor || "");
    formData.append("quantity", 1);
    textInputs.forEach((text) => formData.append("texts[]", text));
    imageFiles.forEach((file) => { if (file) formData.append("images", file); });

    dispatch(addToCart(formData))
      .unwrap()
      .then(() => {
        dispatch(fetchCart());
        toast.success("Item added to cart 🛒");
        const modalEl = document.getElementById("header_exampleModal");
        if (modalEl) {
          const modalInstance =
            window.bootstrap?.Modal.getInstance(modalEl) ||
            new window.bootstrap.Modal(modalEl);
          modalInstance.hide();
        }
        resetCustomizer();
      })
      .catch(() => toast.error("Failed to add item"));
  };

  const handleOpenViewModal = (e, product) => {
    e.stopPropagation();
    if (product?.images && product.images.length > 0) {
      setViewImages(product.images.map(img => ({
        id: img,
        url: `${API_URL}/uploads/${img.replace(/\\/g, '/')}`,
        thumb: `${API_URL}/uploads/${img.replace(/\\/g, '/')}`
      })));
      setSelectedViewImage(0);
    } else {
      setViewImages([]);
    }
  };

 const loading = productsLoading;

  return (
    <div className="DR_body">
      <div className="DR_container">

        {/* Search Section */}
        <div className="DR_searchSection">
          <div className="DR_searchWrapper">
            {/* Category Dropdown */}
            <div className="DR_categoryDropdown" ref={dropdownRef}>
              <button
                className="DR_dropdownButton"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="DR_dropdownButtonContent">
                  <span className="DR_categoryIcon">{getSelectedCategory().icon}</span>
                  <span>{getSelectedCategory().name}</span>
                </span>
                <svg
                  className={`DR_dropdownArrow ${isDropdownOpen ? 'DR_open' : ''}`}
                  width="12" height="8" viewBox="0 0 12 8" fill="none"
                >
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="DR_dropdownMenu">
                  <div className="DR_dropdownMenuInner">
                    {categories.map((category, index) => (
                      <div
                        key={category._id || index}
                        className={`DR_dropdownItem ${selectedCategory === category._id ? 'DR_dropdownItemActive' : ''} ${index === 0 ? 'DR_dropdownItemFirst' : ''}`}
                        onClick={() => handleCategorySelect(category._id)}
                      >
                        <span className="DR_categoryIcon">{category.icon}</span>
                        <span>{category.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Search Input */}
            <div className="DR_searchBox">
              <svg className="DR_searchIcon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                className="DR_searchInput"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button className="DR_clearButton" onClick={handleClearSearch}>✕</button>
              )}
            </div>
          </div>

          {/* Filter Tags */}
          {(searchTerm || selectedCategory) && (
            <div className="DR_filterTags">
              {searchTerm && (
                <div className="DR_filterTag">
                  <i className="iconly-Light-Search"></i> Search: "{searchTerm}"
                  <button className="DR_filterTagButton" onClick={handleClearSearch}>×</button>
                </div>
              )}
              {selectedCategory && (
                <div className="DR_filterTag">
                  Category: {getCategoryLabel(selectedCategory)}
                  <button className="DR_filterTagButton" onClick={handleClearCategory}>×</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* List Header */}
        <div className="DR_listHeader">
          <div>
            <h2 className="DR_listTitle">Search Results</h2>
            <p className="DR_resultsCount">
              {pagination.total} product{pagination.total !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="DR_loadingState">
            <div className="DR_spinner"></div>
            <p>Loading products...</p>
          </div>
        )}

        {/* Product Grid */}
        {!loading && products.length > 0 ? (
          <div className="DR_productsContainer">
            <div className="row gx-xl-4 g-3">
              {products.map((product) => (
                <div
                  key={product._id}
                  className="col-6 col-xl-2 col-lg-2 col-md-4 col-sm-6 m-md-b15 relative mobilecard"
                >
                  <div className="SP_container">
                    {product?.label?.name && (
                      <div className="DR_productLabel">{product.label.name}</div>
                    )}
                    <div className="SP_card">
                      <div className="SP_image_wrapper">

                        {/* ✅ FIXED — safe image with fallback + onError */}
                        <img
                          src={getProductImage(product)}
                          className="SP_frame_Search"
                          alt={product.name || "Product"}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultImage;
                          }}
                        />

                        <div
                          className={`btn-primary meta-icon dz-wishicon ${isFavourite(product._id) ? "active" : ""} ${favLoading ? "disabled" : ""}`}
                          onClick={(e) => handleFavouriteClick(e, product._id)}
                        >
                          <i className="fa-solid fa-heart fill"></i>
                          <i className="fa-regular fa-heart unfill"></i>
                        </div>

                        <div className="btn btn-primary meta-icon dz-carticon">
                          <i className="fa-solid fa-share-nodes"></i>
                        </div>

                        <div
                          className="btn btn-primary meta-icon dz-view"
                          data-bs-toggle="modal"
                          data-bs-target="#header_quick-view"
                          onClick={(e) => handleOpenViewModal(e, product)}
                        >
                          <i className="fa-solid fa-eye"></i>
                        </div>
                      </div>

                      <div className="rowMAIN">
                        <h3 className="SP_title">{product.name}</h3>
                        <div className="SP_price_row">
                          <span className="SP_current_price">
                            ₹{product.sizes?.[0]?.sellingPrice ?? product.sellingPrice}
                          </span>
                          <span className="SP_original_price">
                            ₹{product.sizes?.[0]?.mrp ?? product.mrp}
                          </span>
                        </div>
                        <button
                          className="SP_customize_button"
                          onClick={() => navigate(`/customize/${product._id}`)}
                        >
                          Upload &amp; Customize
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !loading && (
          <div className="DR_emptyState">
            <div className="DR_emptyStateIcon"><i className="iconly-Light-Search"></i></div>
            <div className="DR_emptyStateText">No products found</div>
          </div>
        )}
      </div>

      {/* Customizer Modal */}
      <div className="modal quick-view-modal fade" id="header_exampleModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <i className="icon feather icon-x" />
            </button>
            <div className="modal-body">
              {selectedProduct && (
                <div className="POPframe_body">
                  <div className="POPframe_container">
                    <div className="POPframe_product-section">
                      <div className="POPframe_preview-area">
                        <div className="POPframe_frame-preview">
                          <div className={`POPframe_frame POPframe_${selectedColor}`}>
                            <div className="POPframe_photo-area">
                              {hasMultipleImages() && (
                                <>
                                  <button className="POPframe_arrow-button POPframe_arrow-left" onClick={handlePrevImage}>‹</button>
                                  <button className="POPframe_arrow-button POPframe_arrow-right" onClick={handleNextImage}>›</button>
                                </>
                              )}
                              <img src={getCurrentImage()} alt="Preview" />
                            </div>
                          </div>
                        </div>

                        {images.length > 0 && (
                          <div className="POPframe_option-group">
                            <label className="POPframe_label">Upload Images:</label>
                            <div className="POPframe_image-uploads" style={{
                              gridTemplateColumns: `repeat(${Math.min(images.length, 4)}, 1fr)`
                            }}>
                              {images.map((_, index) => (
                                <div key={index} className="POPframe_upload-item">
                                  <div
                                    className="POPframe_upload-box"
                                    onClick={() => document.getElementById(`header_POPframe_file${index}`).click()}
                                  >
                                    <input
                                      type="file"
                                      id={`header_POPframe_file${index}`}
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(index, e)}
                                      style={{ display: 'none' }}
                                    />
                                    {!images[index] ? (
                                      <span className="POPframe_upload-label">Image {index + 1}</span>
                                    ) : (
                                      <img src={images[index]} alt={`Preview ${index + 1}`} />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="POPframe_options-area">
                        <h2 className="POPframe_heading">{selectedProduct.name}</h2>
                        <div className="POPframe_price_display">
                          <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#4a90e2' }}>
                            ₹{selectedProduct.sellingPrice}
                          </span>
                          <span style={{ fontSize: '18px', textDecoration: 'line-through', color: '#999', marginLeft: '10px' }}>
                            ₹{selectedProduct.mrp}
                          </span>
                        </div>

                        {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                          <div className="POPframe_option-group">
                            <label className="POPframe_label">Frame Size:</label>
                            <select
                              className="POPframe_select"
                              value={mainSize}
                              onChange={(e) => { setMainSize(e.target.value); setValidationErrors([]); }}
                            >
                              {selectedProduct.sizes.map((size) => (
                                <option key={size._id} value={size.name}>{size.name}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {selectedProduct.colors && selectedProduct.colors.length > 0 && (
                          <div className="POPframe_option-group">
                            <label className="POPframe_label">Frame Color:</label>
                            <div className="POPframe_color-selector">
                              {selectedProduct.colors.map((color) => (
                                <div
                                  key={color._id}
                                  className={`POPframe_color-option ${selectedColor === color.code ? 'POPframe_active' : ''}`}
                                  style={{ background: color.code }}
                                  onClick={() => handleColorChange(color.code)}
                                  title={color.name}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {textInputs.map((text, index) => (
                          <div key={index} className="POPframe_option-group">
                            <label className="POPframe_label">Enter Text {index + 1}:</label>
                            <input
                              type="text"
                              className="POPframe_input"
                              placeholder="Your text here..."
                              value={text}
                              onChange={(e) => handleTextChange(index, e.target.value)}
                            />
                          </div>
                        ))}

                        {validationErrors.length > 0 && (
                          <div style={{ backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', padding: '12px', marginBottom: '16px' }}>
                            {validationErrors.map((error, index) => (
                              <div key={index} style={{ color: '#c33', fontSize: '14px', marginBottom: index < validationErrors.length - 1 ? '8px' : '0', display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: '8px' }}>⚠️</span>{error}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="POPframe_buttons">
                          <button
                            disabled={cartLoading}
                            onClick={handleAddToCart}
                            className="btn btn-secondary text-uppercase AddCArd"
                          >
                            {cartLoading ? "Adding..." : "Add to Cart"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <div className="modal quick-view-modal fade" id="header_quick-view" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <i className="icon feather icon-x" />
            </button>
            <div className="modal-body">
              {viewImages.length > 0 ? (
                <div className="view-gallery-container">
                  <div className="view-thumbnail-sidebar">
                    {viewImages.map((image, index) => (
                      <button
                        key={image.id}
                        onClick={() => setSelectedViewImage(index)}
                        className={`view-thumbnail-button ${selectedViewImage === index ? 'active' : ''}`}
                        type="button"
                      >
                        <img src={image.thumb} alt={`Thumbnail ${index + 1}`} />
                      </button>
                    ))}
                  </div>
                  <div className="view-main-display">
                    <div className="view-main-image-wrapper">
                      <img src={viewImages[selectedViewImage]?.url} alt={`Product image ${selectedViewImage + 1}`} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center p-5">No images available</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content POP">
              <Popuplogin onClose={() => setShowLoginModal(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;